import { useState, useEffect, useCallback } from 'react';
import { 
  getDeadlines, 
  getUpcomingDeadlines, 
  countUrgentDeadlines,
  createDeadline,
  updateDeadline,
  deleteDeadline,
  markDeadlineComplete,
  getDeadlineTemplates,
  createDeadlineFromTemplate
} from '@/services/deadlineService';
import { useAuth } from '@/hooks/useAuth';
import type { 
  Deadline, 
  DeadlineTemplate,
  CreateDeadlineInput, 
  UpdateDeadlineInput,
  DeadlineStatus,
  DeadlineCategory
} from '@/types/deadlines';

export function useDeadlines() {
  const { user, profile } = useAuth();
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [templates, setTemplates] = useState<DeadlineTemplate[]>([]);
  const [urgentCount, setUrgentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const autonomousCommunity = (profile as any)?.autonomous_community || 'otra';
  const organizationId = (profile as any)?.organization_id || undefined;

  // Cargar plazos
  const loadDeadlines = useCallback(async (
    status?: DeadlineStatus | DeadlineStatus[],
    category?: DeadlineCategory
  ) => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getDeadlines(status, category);
      setDeadlines(data);
    } catch (err) {
      setError('Error al cargar los plazos');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Cargar plantillas
  const loadTemplates = useCallback(async (category?: DeadlineCategory) => {
    try {
      // Incluir plantillas nacionales + de la CCAA del usuario
      const scopes: ('nacional' | 'andalucia' | 'extremadura')[] = ['nacional'];
      if (autonomousCommunity === 'andalucia') scopes.push('andalucia');
      if (autonomousCommunity === 'extremadura') scopes.push('extremadura');
      
      const data = await getDeadlineTemplates(scopes, category);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  }, [autonomousCommunity]);

  // Contar urgentes
  const loadUrgentCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const count = await countUrgentDeadlines();
      setUrgentCount(count);
    } catch (err) {
      console.error('Error counting urgent deadlines:', err);
    }
  }, [user]);

  // Crear plazo
  const create = useCallback(async (input: CreateDeadlineInput) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    const deadline = await createDeadline(input, user.id);
    if (deadline) {
      setDeadlines(prev => [...prev, deadline].sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      ));
      await loadUrgentCount();
    }
    return deadline;
  }, [user, loadUrgentCount]);

  // Crear desde plantilla
  const createFromTemplate = useCallback(async (template: DeadlineTemplate) => {
    if (!user) throw new Error('Usuario no autenticado');
    
    const deadline = await createDeadlineFromTemplate(
      template, 
      user.id, 
      autonomousCommunity,
      organizationId
    );
    if (deadline) {
      setDeadlines(prev => [...prev, deadline].sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      ));
      await loadUrgentCount();
    }
    return deadline;
  }, [user, autonomousCommunity, organizationId, loadUrgentCount]);

  // Actualizar plazo
  const update = useCallback(async (id: string, input: UpdateDeadlineInput) => {
    const deadline = await updateDeadline(id, input);
    if (deadline) {
      setDeadlines(prev => prev.map(d => d.id === id ? deadline : d));
      await loadUrgentCount();
    }
    return deadline;
  }, [loadUrgentCount]);

  // Eliminar plazo
  const remove = useCallback(async (id: string) => {
    const success = await deleteDeadline(id);
    if (success) {
      setDeadlines(prev => prev.filter(d => d.id !== id));
      await loadUrgentCount();
    }
    return success;
  }, [loadUrgentCount]);

  // Marcar como completado
  const complete = useCallback(async (id: string) => {
    const deadline = await markDeadlineComplete(id);
    if (deadline) {
      setDeadlines(prev => prev.map(d => d.id === id ? deadline : d));
      await loadUrgentCount();
    }
    return deadline;
  }, [loadUrgentCount]);

  // Obtener próximos vencimientos
  const getUpcoming = useCallback(async (days: number = 7) => {
    return await getUpcomingDeadlines(days);
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    if (user) {
      loadDeadlines();
      loadTemplates();
      loadUrgentCount();
    }
  }, [user, loadDeadlines, loadTemplates, loadUrgentCount]);

  return {
    deadlines,
    templates,
    urgentCount,
    isLoading,
    error,
    loadDeadlines,
    loadTemplates,
    create,
    createFromTemplate,
    update,
    remove,
    complete,
    getUpcoming,
    refresh: () => {
      loadDeadlines();
      loadUrgentCount();
    },
  };
}
